import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { brand } from "../content/site";
import { PageHeader } from "../components/PageHeader";
import { Reveal } from "../components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border border-stone-900/20 bg-transparent px-4 py-3.5 text-sm text-stone-900 placeholder:text-stone-400 focus:border-emerald-900 focus:outline-none transition-colors duration-300";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", topic: "General", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you. Your message has been sent. We aim to respond within 1–2 working days.");
      setForm({ name: "", email: "", topic: "General", message: "" });
    } catch {
      toast.error(`Sorry — your message could not be sent. Please try again, or email us directly at ${brand.email}.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="We're here to help."
        body="Whether you have a question about our products, your order or EL Well, we'd love to hear from you."
      />
      <section className="mx-auto grid max-w-7xl gap-16 px-4 py-16 sm:px-8 lg:grid-cols-5 lg:gap-20 lg:px-16 lg:py-24">
        <Reveal className="lg:col-span-3">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className={inputCls}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Email
                </label>
                <input
                  id="contact-email"
                  type="email"
                  data-testid="contact-email-input"
                  required
                  value={form.email}
                  onChange={set("email")}
                  className={inputCls}
                  placeholder="you@example.co.uk"
                />
              </div>
            </div>
            <div>
              <label htmlFor="contact-topic" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Topic
              </label>
              <select
                id="contact-topic"
                data-testid="contact-topic-select"
                value={form.topic}
                onChange={set("topic")}
                className={inputCls}
              >
                {["General", "Order enquiry", "Product question", "Wholesale", "Press"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Message
              </label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={6}
                value={form.message}
                onChange={set("message")}
                className={inputCls}
                placeholder="How can we help?"
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="bg-stone-900 px-9 py-4 font-mono text-[11px] font-medium uppercase tracking-[0.25em] text-stone-50 transition-colors duration-300 hover:bg-emerald-900 disabled:opacity-50"
            >
              {sending ? "Sending…" : "Send Message"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-2">
          <aside className="border border-stone-900/10 bg-[#F3EFE6] p-10">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-900/80">Direct</p>
            <a
              href={`mailto:${brand.email}`}
              data-testid="contact-email-link"
              className="mt-4 block font-serif text-2xl font-light text-stone-900 underline-offset-4 transition-colors duration-300 hover:text-emerald-900 hover:underline"
            >
              {brand.email}
            </a>
            <div className="mt-8 space-y-4 border-t border-stone-900/10 pt-8 text-sm leading-relaxed text-stone-600">
              <p className="font-medium text-stone-900">EL Well Wellness Ltd</p>
              <p>
                10 Beechcroft View
                <br />
                Leeds
                <br />
                LS11 0LN
              </p>
              <p>{brand.domain}</p>
              <p>We aim to respond within 1–2 working days.</p>
            </div>
          </aside>
        </Reveal>
      </section>
    </div>
  );
}
